import { Injectable, inject } from '@angular/core'
import { BehaviorSubject, Subject } from 'rxjs'
import { generateUuid } from '@core/global-util'
import { Workspace } from '@core/models'
import { RecordCardNavigationService } from '@app/pages/service/record-card-navigation.service'

export interface PinRecord {
    guid: string
    name: string
}

export interface PinDraftRecord {
    name: string
    data: DraftPinRecord
    guid: string
}

export interface DraftPinRecord {
    schemaGuid: string
    workspaces: Workspace[]
    selectedWorkspace: Workspace
    name: string | null
    fieldValues: { [fieldGuid: string]: string }
}

export interface PinObject {
    type: PinObjectType
    record: PinRecord | PinDraftRecord
}

export enum PinObjectType {
    CREATED,
    DRAFT,
}

@Injectable({
    providedIn: 'root',
})
export class PinRecordService {
    private readonly recordCardNavigationService = inject(RecordCardNavigationService)

    private readonly pinObjects: Map<string, PinObject> = new Map()
    public readonly pinObjects$: Subject<PinObject[]> = new BehaviorSubject<PinObject[]>([])

    isRecordPinned(guid: string) {
        return this.pinObjects.has(guid)
    }

    openPinItem(guid: string) {
        this.recordCardNavigationService.openRecordCard({ recordGuid: guid, isWorkspace: true })
    }

    pinDraftRecord(data: DraftPinRecord) {
        this.updateDraftRecord(generateUuid(), data)
    }

    updateDraftRecord(guid: string, data: DraftPinRecord) {
        const pinRecord = {
            name: data.name ?? 'Draft',
            data,
            guid,
        }

        this.pinObjects.set(guid, { type: PinObjectType.DRAFT, record: pinRecord })
        this.pinObjects$.next(Array.from(this.pinObjects.values()))
    }

    pinRecord(guid: string, name: string) {
        if (this.pinObjects.has(guid)) return
        const pinRecord = {
            guid,
            name,
        }

        this.pinObjects.set(guid, { type: PinObjectType.CREATED, record: pinRecord })
        this.pinObjects$.next(Array.from(this.pinObjects.values()))
    }

    removePinRecord(guid: string) {
        this.pinObjects.delete(guid)
        this.pinObjects$.next(Array.from(this.pinObjects.values()))
    }
}
